import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FolderPlus, Plus, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useTheme } from "@/hooks/useTheme";
import {
  buildNoteFolderTree,
  createNote,
  createNoteFolder,
  deleteNoteFolder,
  listNoteFolders,
  listNotes,
  moveNote,
  moveNoteFolder,
  renameNoteFolder,
  reorderNote,
  type Note,
  type NoteFolder,
} from "@/lib/composerNotes";
import { NotesFolderTree, type FocusTarget } from "./NotesFolderTree";
import { NoteEditorSheet } from "./NoteEditorSheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type Props = {
  workspaceId: string;
};

const QK = {
  folders: (ws: string) => ["composer-notes-folders", ws] as const,
  notes: (ws: string) => ["composer-notes", ws] as const,
};

export const NotesPanel = ({ workspaceId }: Props) => {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { resolved } = useTheme();
  const [focused, setFocused] = useState<FocusTarget>({ kind: "root" });
  const [search, setSearch] = useState("");
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [renameTarget, setRenameTarget] = useState<NoteFolder | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [newFolderParent, setNewFolderParent] = useState<{ folder: NoteFolder | null } | null>(null);
  const [newFolderName, setNewFolderName] = useState("");

  const foldersQ = useQuery({
    queryKey: QK.folders(workspaceId),
    queryFn: () => listNoteFolders(workspaceId),
    enabled: !!workspaceId,
  });
  const notesQ = useQuery({
    queryKey: QK.notes(workspaceId),
    queryFn: () => listNotes(workspaceId),
    enabled: !!workspaceId,
  });

  const folders = useMemo(() => foldersQ.data ?? [], [foldersQ.data]);
  const notes = useMemo(() => notesQ.data ?? [], [notesQ.data]);

  const tree = useMemo(() => buildNoteFolderTree(folders, notes), [folders, notes]);


  const invalidate = () => {
    qc.invalidateQueries({ queryKey: QK.folders(workspaceId) });
    qc.invalidateQueries({ queryKey: QK.notes(workspaceId) });
  };

  const createNoteMut = useMutation({
    mutationFn: () =>
      createNote({
        workspaceId,
        folderId: focused.kind === "folder" ? focused.folderId : null,
      }),
    onSuccess: (note) => {
      invalidate();
      setEditingNote(note);
    },
    onError: (err: Error) =>
      toast({ title: "Couldn't create note", description: err.message, variant: "destructive" }),
  });

  const moveNoteMut = useMutation({
    mutationFn: ({ id, folderId }: { id: string; folderId: string | null }) => moveNote(id, folderId),
    onSuccess: () => invalidate(),
    onError: (err: Error) =>
      toast({ title: "Couldn't move note", description: err.message, variant: "destructive" }),
  });

  const moveFolderMut = useMutation({
    mutationFn: ({ id, newParentId }: { id: string; newParentId: string | null }) =>
      moveNoteFolder({ id, newParentId }),
    onSuccess: () => invalidate(),
    onError: (err: Error) =>
      toast({ title: "Couldn't move folder", description: err.message, variant: "destructive" }),
  });

  const reorderNoteMut = useMutation({
    mutationFn: ({ id, sortOrder }: { id: string; sortOrder: number }) => reorderNote(id, sortOrder),
    onSuccess: () => invalidate(),
    onError: (err: Error) =>
      toast({ title: "Couldn't reorder note", description: err.message, variant: "destructive" }),
  });

  const deleteFolderMut = useMutation({
    mutationFn: (id: string) => deleteNoteFolder(id),
    onSuccess: () => invalidate(),
    onError: (err: Error) =>
      toast({ title: "Couldn't delete folder", description: err.message, variant: "destructive" }),
  });

  const renameFolderMut = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => renameNoteFolder(id, name),
    onSuccess: () => {
      invalidate();
      setRenameTarget(null);
    },
    onError: (err: Error) =>
      toast({ title: "Couldn't rename", description: err.message, variant: "destructive" }),
  });

  const createFolderMut = useMutation({
    mutationFn: ({ name, parentId }: { name: string; parentId: string | null }) =>
      createNoteFolder({ workspaceId, name, parentFolderId: parentId }),
    onSuccess: () => {
      invalidate();
      setNewFolderParent(null);
      setNewFolderName("");
    },
    onError: (err: Error) =>
      toast({ title: "Couldn't create folder", description: err.message, variant: "destructive" }),
  });

  const addButtonClass = cn(
    "h-9 w-9 rounded-md inline-flex items-center justify-center transition-colors",
    resolved === "dark"
      ? "bg-primary text-primary-foreground hover:bg-primary/90"
      : "bg-blue-600 text-white hover:bg-blue-700",
  );

  return (
    <div className="flex h-full flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search notes"
            className="h-9 pl-7 text-sm"
          />
        </div>
        <button
          type="button"
          aria-label="New note"
          className={addButtonClass}
          onClick={() => createNoteMut.mutate()}
          disabled={createNoteMut.isPending}
        >
          <Plus className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto rounded-md border border-border bg-card p-1.5">
        <NotesFolderTree
          tree={tree}
          notes={notes}
          search={search}
          focused={focused}
          onFocus={setFocused}
          onOpenNote={(n) => setEditingNote(n)}
          onCreateFolder={(parent) => {
            setNewFolderName("");
            setNewFolderParent({ folder: parent });
          }}
          onRenameFolder={(f) => {
            setRenameValue(f.name);
            setRenameTarget(f);
          }}
          onDeleteFolder={(f) => deleteFolderMut.mutate(f.id)}
          onMoveNote={(id, folderId) => moveNoteMut.mutate({ id, folderId })}
          onMoveFolder={(id, newParentId) => moveFolderMut.mutate({ id, newParentId })}
          onReorderNote={(id, sortOrder) => reorderNoteMut.mutate({ id, sortOrder })}
        />

        <button
          type="button"
          onClick={() => {
            setNewFolderName("");
            setNewFolderParent({ folder: null });
          }}
          className="mt-2 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted/60 hover:text-foreground"
        >
          <FolderPlus className="h-3.5 w-3.5" /> New folder
        </button>
      </div>

      <NoteEditorSheet
        note={editingNote}
        open={editingNote !== null}
        onOpenChange={(o) => {
          if (!o) setEditingNote(null);
        }}
        onChanged={invalidate}
      />

      {/* Rename folder dialog */}
      <Dialog open={renameTarget !== null} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename folder</DialogTitle>
            <DialogDescription>Give this folder a new name.</DialogDescription>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter" && renameTarget) {
                renameFolderMut.mutate({ id: renameTarget.id, name: renameValue });
              }
            }}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenameTarget(null)}>Cancel</Button>
            <Button
              onClick={() => renameTarget && renameFolderMut.mutate({ id: renameTarget.id, name: renameValue })}
              disabled={!renameValue.trim()}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New folder dialog */}
      <Dialog open={newFolderParent !== null} onOpenChange={(o) => !o && setNewFolderParent(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {newFolderParent?.folder ? `New sub-folder in "${newFolderParent.folder.name}"` : "New folder"}
            </DialogTitle>
            <DialogDescription>Organize your notes into folders.</DialogDescription>
          </DialogHeader>
          <Input
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            autoFocus
            placeholder="Folder name"
            onKeyDown={(e) => {
              if (e.key === "Enter" && newFolderName.trim()) {
                createFolderMut.mutate({ name: newFolderName, parentId: newFolderParent?.folder?.id ?? null });
              }
            }}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewFolderParent(null)}>Cancel</Button>
            <Button
              onClick={() =>
                createFolderMut.mutate({ name: newFolderName, parentId: newFolderParent?.folder?.id ?? null })
              }
              disabled={!newFolderName.trim()}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
